import React, { useRef } from 'react'
import { useModalDialog } from '../../../hooks'
import { PersonEditForm, CompanyEditForm } from '../../../components'
import { BulkPersonImport, NetworkingEventForm } from '../components'
import { networkingEventService } from '../../../services'

// hideModalDialog() must be called inside the caller's own .then() on
// success — showModalDialog never auto-closes on submit (see root CLAUDE.md
// gotcha, same convention as clientCases/useClientCaseDialog).
export const useNetworkDialog = () => {
  const { showModalDialog, hideModalDialog, setLoadingModalDialog } = useModalDialog()

  const dialogRef = useRef({ submit: () => Promise.resolve() })

  const showPersonDialog = (person, onSaved) => {
    showModalDialog({
      title: person ? 'Redigera person' : 'Ny person',
      Content: () => (
        <PersonEditForm
          person={person}
          dialogRef={dialogRef}
          hideModalDialog={hideModalDialog}
          setLoadingModalDialog={setLoadingModalDialog}
          onSaved={onSaved}
        />
      ),
      submitLabel: 'Spara',
      submitLabelLoading: 'Sparar...',
      cancelLabel: 'Avbryt',
      onSubmit: () => dialogRef.current.submit(),
      onCancel: hideModalDialog,
    })
  }

  const showCompanyDialog = (company, onSaved) => {
    showModalDialog({
      title: company ? 'Redigera företag' : 'Nytt företag',
      Content: () => (
        <CompanyEditForm
          company={company}
          dialogRef={dialogRef}
          hideModalDialog={hideModalDialog}
          setLoadingModalDialog={setLoadingModalDialog}
          onSaved={onSaved}
        />
      ),
      submitLabel: 'Spara',
      submitLabelLoading: 'Sparar...',
      cancelLabel: 'Avbryt',
      onSubmit: () => dialogRef.current.submit(),
      onCancel: hideModalDialog,
    })
  }

  const showBulkImportDialog = (onImported) => {
    showModalDialog({
      title: 'Lägg till flera personer',
      Content: () => (
        <BulkPersonImport
          dialogRef={dialogRef}
          hideModalDialog={hideModalDialog}
          setLoadingModalDialog={setLoadingModalDialog}
          onImported={onImported}
        />
      ),
      submitLabel: 'Spara',
      submitLabelLoading: 'Sparar...',
      cancelLabel: 'Avbryt',
      onSubmit: () => dialogRef.current.submit(),
      onCancel: hideModalDialog,
    })
  }

  // Register (or edit) a "Nätverkande" entry on a person. `entry` prefills +
  // switches to edit; pass null for a fresh create.
  const showNetworkingDialog = (person, entry, onSaved) => {
    showModalDialog({
      title: entry ? 'Redigera nätverkande' : 'Registrera nätverkande',
      Content: () => (
        <NetworkingEventForm
          person={person}
          entry={entry}
          dialogRef={dialogRef}
          hideModalDialog={hideModalDialog}
          setLoadingModalDialog={setLoadingModalDialog}
          onSaved={onSaved}
        />
      ),
      submitLabel: 'Spara',
      submitLabelLoading: 'Sparar...',
      cancelLabel: 'Avbryt',
      onSubmit: () => dialogRef.current.submit(),
      onCancel: hideModalDialog,
    })
  }

  // Confirm + delete a networking entry (append a retraction, by source_ref).
  const showDeleteNetworkingDialog = (entry, onDeleted) => {
    showModalDialog({
      title: 'Ta bort nätverkande',
      Content: () => (
        <div>Detta tar bort nätverkandet från personens tidslinje och postar ett meddelande i Teams-kanalen.</div>
      ),
      onSubmit: () => networkingEventService.remove(entry.source_ref).then(() => { hideModalDialog(); onDeleted && onDeleted() }),
      submitLabelLoading: 'Tar bort...',
    })
  }

  // Accepts either a single person or an array (bulk delete from the list's
  // "Åtgärder" menu) — onSubmit always receives an array.
  const showDeletePersonDialog = (persons, onSubmit) => {
    const list = Array.isArray(persons) ? persons : [persons]
    showModalDialog({
      title: 'Ta bort person',
      Content: () => (
        <div>
          {list.length === 1
            ? <>Detta tar permanent bort <strong>{list[0].person_name}</strong> från Nätverket.</>
            : <>Detta tar permanent bort {list.length} personer från Nätverket.</>}
        </div>
      ),
      onSubmit: () => onSubmit(list),
      submitLabelLoading: 'Tar bort...',
    })
  }

  const showDeleteCompanyDialog = (companies, onSubmit) => {
    const list = Array.isArray(companies) ? companies : [companies]
    showModalDialog({
      title: 'Ta bort företag',
      Content: () => (
        <div>
          {list.length === 1
            ? <>Detta tar permanent bort <strong>{list[0].company_name}</strong> från Nätverket.</>
            : <>Detta tar permanent bort {list.length} företag från Nätverket.</>}
        </div>
      ),
      onSubmit: () => onSubmit(list),
      submitLabelLoading: 'Tar bort...',
    })
  }

  return {
    showPersonDialog,
    showCompanyDialog,
    showBulkImportDialog,
    showNetworkingDialog,
    showDeleteNetworkingDialog,
    showDeletePersonDialog,
    showDeleteCompanyDialog,
  }
}
